import React, { useState, useEffect } from "react";
import { useMutation, useLazyQuery } from "@apollo/client";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Alert, AlertDescription } from "./ui/alert";
import {
  Shield,
  ShieldCheck,
  ShieldAlert,
  Clock,
  Tablet,
  MapPin,
  AlertCircle,
  XCircle,
  Loader2,
  Wifi,
  Key,
  RefreshCw,
} from "lucide-react";
import {
  REGISTER_DEVICE_WITH_TOKEN,
  GET_DEVICE_STATUS_BY_TOKEN,
} from "../graphql/queries";
import { collectDeviceFingerprint } from "../utils/deviceFingerprinting";

interface DeviceInfo {
  id: string;
  token: string;
  status:
    | "TOKEN_GENERATED"
    | "REGISTERED"
    | "APPROVED"
    | "REJECTED"
    | "BLOCKED";
  deviceName?: string;
  location?: string;
  fingerprint?: string;

  // Auto-collected data
  userAgent?: string;
  screenResolution?: string;
  timezone?: string;
  ipAddress?: string;
  macAddress?: string;

  registeredAt?: string;
  createdAt: string;
}

interface DeviceRegistrationProps {
  onDeviceVerified?: (device: DeviceInfo) => void;
  onRegistrationComplete?: () => void;
  onStatusUpdated?: () => void;
}

const DeviceRegistration: React.FC<DeviceRegistrationProps> = ({
  onDeviceVerified,
  onRegistrationComplete,
  onStatusUpdated,
}) => {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo | null>(null);
  const [isRegistering, setIsRegistering] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [token, setToken] = useState("");
  const [fingerprint, setFingerprint] = useState("");
  const [error, setError] = useState<string>("");

  const [registerDeviceWithToken] = useMutation(REGISTER_DEVICE_WITH_TOKEN);
  const [getDeviceStatus] = useLazyQuery(GET_DEVICE_STATUS_BY_TOKEN);

  // Function to refresh device status from API
  const refreshDeviceStatus = async () => {
    if (!deviceInfo?.token) return;

    setIsRefreshing(true);
    try {
      const { data } = await getDeviceStatus({
        variables: { token: deviceInfo.token },
        fetchPolicy: "network-only", // Always fetch fresh data
      });

      if (data?.deviceByToken) {
        const updatedDevice = {
          ...deviceInfo,
          ...data.deviceByToken,
          createdAt: deviceInfo.createdAt,
        };

        setDeviceInfo(updatedDevice);

        // Notify parent component about status update
        if (onStatusUpdated) {
          onStatusUpdated();
        }
      }
    } catch (error) {
      console.error("Error refreshing device status:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Generate proper device fingerprint
  useEffect(() => {
    const generateFingerprint = async () => {
      try {
        const deviceFingerprint = await collectDeviceFingerprint();
        // Generate the same hash as the API does - use only most stable characteristics
        const fingerprintData = {
          userAgent: deviceFingerprint.userAgent,
          platform: deviceFingerprint.platform,
          screen: {
            width: deviceFingerprint.screen.width,
            height: deviceFingerprint.screen.height,
            colorDepth: deviceFingerprint.screen.colorDepth,
          },
          timezone: deviceFingerprint.timezone,
        };

        const fingerprintString = JSON.stringify(fingerprintData);
        // Use a simple hash for now - this should match API's crypto.createHash
        const encoder = new TextEncoder();
        const data = encoder.encode(fingerprintString);
        const hashBuffer = await crypto.subtle.digest("SHA-256", data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray
          .map((b) => b.toString(16).padStart(2, "0"))
          .join("");

        setFingerprint(hashHex);
      } catch (error) {
        console.error("Error generating fingerprint:", error);
        // Fallback to simple fingerprint
        const fallback = `tablet-${Date.now()}`;
        setFingerprint(fallback);
      }
    };

    generateFingerprint();
  }, []);

  // Device verification will be handled by the parent component via API

  const handleTokenSubmit = async () => {
    if (!token.trim()) {
      return;
    }

    setIsRegistering(true);
    setError("");

    try {
      const deviceFingerprint = await collectDeviceFingerprint();

      const result = await registerDeviceWithToken({
        variables: {
          data: {
            token: token.trim().toUpperCase(),
            fingerprint,
            userAgent: deviceFingerprint.userAgent,
            screenResolution: `${deviceFingerprint.screen.width}x${deviceFingerprint.screen.height}`,
            timezone: deviceFingerprint.timezone,
          },
        },
      });

      if (result.data?.registerDeviceWithToken?.success) {
        const device = result.data.registerDeviceWithToken.device;

        const registeredDevice: DeviceInfo = {
          id: device.id,
          token: device.token,
          fingerprint: device.fingerprint,
          status: device.status as "REGISTERED",
          deviceName: device.deviceName,
          location: device.location,
          userAgent: device.userAgent,
          screenResolution: device.screenResolution,
          timezone: device.timezone,
          registeredAt: device.registeredAt,
          createdAt: device.createdAt || new Date().toISOString(),
        };

        // Device registered successfully

        setDeviceInfo(registeredDevice);

        // Clear form
        setToken("");

        // After successful registration, trigger device verification
        // The device needs to be approved by admin before it can be used
        if (onDeviceVerified) {
          onDeviceVerified(registeredDevice);
        }
      } else {
        throw new Error(
          result.data?.registerDeviceWithToken?.message ||
            "Rejestracja nie powiodła się"
        );
      }
    } catch (error: any) {
      console.error("Registration error:", error);
      let errorMessage =
        "Nieprawidłowy token urządzenia. Sprawdź token i spróbuj ponownie.";

      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        errorMessage = error.graphQLErrors[0].message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
    } finally {
      setIsRegistering(false);
    }
  };

  const handleClearRegistration = () => {
    if (
      window.confirm("Czy na pewno chcesz wyczyścić rejestrację urządzenia?")
    ) {
      setDeviceInfo(null);
      // Notify parent to retry verification
      if (onStatusUpdated) {
        onStatusUpdated();
      }
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "APPROVED":
        return <ShieldCheck className="w-6 h-6 text-green-500" />;
      case "REGISTERED":
        return <Shield className="w-6 h-6 text-blue-500" />;
      case "TOKEN_GENERATED":
        return <Shield className="w-6 h-6 text-yellow-500" />;
      case "REJECTED":
        return <XCircle className="w-6 h-6 text-red-500" />;
      case "BLOCKED":
        return <ShieldAlert className="w-6 h-6 text-red-600" />;
      default:
        return <Shield className="w-6 h-6 text-gray-500" />;
    }
  };

  const getStatusMessage = (status: string) => {
    switch (status) {
      case "APPROVED":
        return {
          title: "Urządzenie zatwierdzone",
          message:
            "Twoje urządzenie zostało zatwierdzone i może być używane w systemie.",
          color: "text-green-600",
        };
      case "REGISTERED":
        return {
          title: "Urządzenie zarejestrowane",
          message:
            "Twoje urządzenie zostało zarejestrowane i oczekuje na zatwierdzenie przez administratora.",
          color: "text-blue-600",
        };
      case "TOKEN_GENERATED":
        return {
          title: "Token wygenerowany",
          message:
            "Token został utworzony, ale urządzenie nie zostało jeszcze zarejestrowane.",
          color: "text-yellow-600",
        };
      case "REJECTED":
        return {
          title: "Urządzenie odrzucone",
          message:
            "Rejestracja tego urządzenia została odrzucona. Skontaktuj się z administratorem.",
          color: "text-red-600",
        };
      case "BLOCKED":
        return {
          title: "Urządzenie zablokowane",
          message:
            "To urządzenie zostało zablokowane i nie może być używane w systemie.",
          color: "text-red-600",
        };
      default:
        return {
          title: "Nieznany status",
          message: "Status urządzenia jest nieznany.",
          color: "text-gray-600",
        };
    }
  };

  // Show device registration status if available
  if (deviceInfo) {
    const statusInfo = getStatusMessage(deviceInfo.status);

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 flex items-center justify-center">
        <Card className="w-full max-w-md mx-auto shadow-lg">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              {getStatusIcon(deviceInfo.status)}
            </div>
            <CardTitle className="text-xl">Status urządzenia</CardTitle>
            <CardDescription>
              Informacje o zarejestrowanym urządzeniu
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className={statusInfo.color}>
                <strong>{statusInfo.title}</strong>
                <br />
                {statusInfo.message}
              </AlertDescription>
            </Alert>

            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2">
                <Key className="w-4 h-4 text-gray-500" />
                <span>
                  <strong>Token:</strong> {deviceInfo.token}
                </span>
              </div>

              {deviceInfo.deviceName && (
                <div className="flex items-center gap-2">
                  <Tablet className="w-4 h-4 text-gray-500" />
                  <span>
                    <strong>Nazwa:</strong> {deviceInfo.deviceName}
                  </span>
                </div>
              )}

              {deviceInfo.location && (
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-500" />
                  <span>
                    <strong>Lokalizacja:</strong> {deviceInfo.location}
                  </span>
                </div>
              )}

              {deviceInfo.fingerprint && (
                <div className="flex items-start gap-2">
                  <Wifi className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <strong>ID urządzenia:</strong>
                    <div className="mt-1 p-2 bg-gray-100 rounded text-xs font-mono break-all overflow-x-auto max-h-20 overflow-y-auto">
                      {deviceInfo.fingerprint}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-500" />
                <span>
                  <strong>
                    {deviceInfo.registeredAt ? "Zarejestrowano:" : "Utworzono:"}
                  </strong>{" "}
                  {new Date(
                    deviceInfo.registeredAt || deviceInfo.createdAt
                  ).toLocaleString("pl-PL")}
                </span>
              </div>
            </div>

            {/* Technical details */}
            {(deviceInfo.userAgent ||
              deviceInfo.screenResolution ||
              deviceInfo.timezone) && (
              <div className="border-t pt-3 space-y-2">
                <h4 className="text-sm font-medium text-gray-600">
                  Dane techniczne:
                </h4>
                <div className="space-y-1 text-xs text-gray-500">
                  {deviceInfo.screenResolution && (
                    <div>
                      <strong>Rozdzielczość:</strong>{" "}
                      {deviceInfo.screenResolution}
                    </div>
                  )}
                  {deviceInfo.timezone && (
                    <div>
                      <strong>Strefa czasowa:</strong> {deviceInfo.timezone}
                    </div>
                  )}
                  {deviceInfo.userAgent && (
                    <div>
                      <strong>Przeglądarka:</strong>{" "}
                      {deviceInfo.userAgent.substring(0, 50)}...
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="pt-4 space-y-2">
              <Button
                onClick={refreshDeviceStatus}
                variant="outline"
                className="w-full"
                disabled={isRefreshing}
              >
                <RefreshCw
                  className={`w-4 h-4 mr-2 ${
                    isRefreshing ? "animate-spin" : ""
                  }`}
                />
                {isRefreshing ? "Odświeżanie..." : "Odśwież status"}
              </Button>
              <Button
                onClick={handleClearRegistration}
                variant="outline"
                className="w-full text-red-600 hover:text-red-700"
              >
                Wyczyść rejestrację
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Token input form
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 flex items-center justify-center">
      <Card className="w-full max-w-md mx-auto shadow-lg">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Key className="w-12 h-12 text-blue-600" />
          </div>
          <CardTitle className="text-xl">Rejestracja urządzenia</CardTitle>
          <CardDescription>
            Wprowadź token otrzymany od administratora
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleTokenSubmit();
            }}
            className="space-y-4"
          >
            <div>
              <Label htmlFor="token">Token urządzenia *</Label>
              <Input
                id="token"
                value={token}
                onChange={(e) => setToken(e.target.value.toUpperCase())}
                placeholder="np. ABC123"
                required
                disabled={isRegistering}
                className="text-center text-lg font-mono tracking-wider"
                maxLength={6}
              />
              <p className="text-sm text-muted-foreground mt-1">
                Wpisz 6-znakowy token otrzymany od administratora
              </p>
            </div>

            {error && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-red-600">
                  {error}
                </AlertDescription>
              </Alert>
            )}

            <div className="pt-4">
              <Button
                type="submit"
                className="w-full"
                disabled={isRegistering || !token.trim()}
              >
                {isRegistering ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Weryfikowanie tokenu...
                  </>
                ) : (
                  <>
                    <Key className="w-4 h-4 mr-2" />
                    Zarejestruj z tokenem
                  </>
                )}
              </Button>
            </div>
          </form>

          <div className="mt-6 p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-600 text-center">
              <strong>ID urządzenia:</strong>
            </p>
            <div className="mt-1 p-2 bg-white rounded text-xs font-mono text-center break-all overflow-x-auto max-h-16 overflow-y-auto">
              {fingerprint}
            </div>
            <p className="text-xs text-gray-500 text-center mt-2">
              Dane techniczne będą automatycznie zebrane po weryfikacji tokenu
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DeviceRegistration;
